import React, { createContext, useContext, useState, useEffect } from "react";

const DICT = {
  en: {
    // Brand
    "app.tagline": "Vineyard operations, all in one place.",
    "app.compliance": "DIR · Cal Labor · QB Ready",
    // Auth
    "auth.signin": "Sign In",
    "auth.signing_in": "Signing in…",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.face_id": "Face ID / Touch ID",
    "auth.demo": "Demo accounts",
    "auth.exit": "Exit",
    // Nav
    "nav.dashboard": "Dashboard",
    "nav.punch_crew": "Time Tracker",
    "nav.crews": "Crews",
    "nav.employees": "Employees",
    "nav.jobcodes": "Job Codes",
    "nav.equipment": "Equipment",
    "nav.ranches": "Ranches",
    "nav.import": "Data Import",
    "nav.users": "Users",
    "nav.expenses": "Expenses",
    "nav.mileage": "Mileage",
    "nav.scouting": "Field Scouting",
    "nav.pest": "Pest & Disease",
    "nav.spray_orders": "Spray Orders",
    "nav.crop": "Crop Estimates",
    "nav.worklist": "Work List",
    "nav.clients": "Clients",
    "nav.client_requests": "Client Requests",
    "nav.estate": "Estate Care",
    "nav.portal": "My Vineyards",
    "nav.client_logins": "Client Logins",
    "nav.budgets": "Budgets",
    "nav.irrigation": "Irrigation",
    "nav.irrigation_log": "Irrigation Log",
    "nav.more": "More",
    "nav.menu": "Menu",
    "nav.timesheets": "Timesheets",
    "nav.qb_export": "QB Export",
    "nav.approvals": "Approvals",
    "nav.signoff": "Sign-Off",
    // Status
    "status.live": "Live",
    "status.syncing": "Syncing…",
    "status.offline": "Offline",
    "status.queued": "queued",
    "status.sync": "Sync",
    // Dashboard
    "dash.operations": "Operations",
    "dash.todays_crew": "Today's Crew",
    "dash.regular_hrs": "Regular Hrs (MTD)",
    "dash.overtime_hrs": "Overtime Hrs",
    "dash.doubletime_hrs": "Double-Time Hrs",
    "dash.active_employees": "Active Employees",
    "dash.penalties": "Cal Labor Code Penalties This Month",
    "dash.meal_penalties": "Meal break penalties",
    "dash.rest_penalties": "Rest break penalties",
    "dash.todays_entries": "Today's Entries",
    "dash.category_totals": "Category Totals (MTD)",
    "dash.cat_hours_only": "Hours by job category",
    "dash.no_entries": "No entries logged today",
    "dash.quick_punch": "Quick Punch",
    "dash.quick_punch_sub": "One-tap clock in / out",
    "dash.on_clock": "On the clock",
    "dash.off_clock": "Off the clock",
    // TimeCard
    "tc.title": "Time Tracker",
    "tc.subtitle": "Real timestamps. No rounding. CA Labor Code compliant.",
    "tc.crew": "Crew",
    "tc.all_employees": "All Employees",
    "tc.members": "members",
    "tc.job_code": "Job Code (applies to new punches)",
    "tc.select_job": "— Select Job —",
    "tc.category": "Category",
    "tc.select_category": "— Select Category —",
    "tc.select_category_first": "— Select a category first —",
    "tc.punch_in": "Start Time",
    "tc.punch_out": "End Time",
    "tc.meal": "Meal",
    "tc.rest": "Rest",
    "tc.equip": "Equip",
    "tc.bulk_actions": "Bulk Crew Actions",
    "tc.bulk_punch_in": "Start Time (All)",
    "tc.bulk_punch_out": "End Time (All)",
    "tc.bulk_punch_in_sel": "Start Time ({n} Selected)",
    "tc.bulk_punch_out_sel": "End Time ({n} Selected)",
    "tc.bulk_meal_start": "Start Meal All",
    "tc.bulk_meal_end": "End Meal All",
    "tc.bulk_rest_start": "Start Rest All",
    "tc.bulk_rest_end": "End Rest All",
    "tc.equipment_for": "Equipment ·",
    "tc.close": "Close",
    "tc.required_equipment": "This job requires equipment",
    "tc.no_equipment_ack": "I acknowledge no equipment was used",
    "tc.in": "In:",
    "tc.tab_crew": "Crew Time",
    "tc.tab_individual": "Individual Time",
    "tc.search_employee": "Search employee by name…",
    "tc.no_match": "No employees match your search",
    "tc.change_job": "Change Job",
    "tc.bulk_change_job": "Change Job All",
    "tc.change_job_hint": "Closes the current job segment and starts a new one on the selected ranch + job code (splits the shift for accurate job costing).",
    "tc.confirm_change_title": "Confirm Job / Ranch Change",
    "tc.confirm_change_body": "This will end the current job segment and start a new one on the selections below. The shift is split for accurate job costing.",
    "tc.confirm_who": "Applies To",
    "tc.confirm_change": "Change Job",
    // Signoff
    "so.title": "Daily Sign-Off",
    "so.subtitle": "Sign your time card and acknowledge daily safety",
    "so.your_name": "Print Your Full Name",
    "so.signature": "Signature",
    "so.clear": "Clear",
    "so.no_injury": "I was NOT injured during my shift today.",
    "so.no_harassment": "I did NOT experience or witness harassment or discrimination today (or I reported it below).",
    "so.notes": "Optional: Report any incident or concern",
    "so.submit": "Submit Sign-Off",
    "so.signed": "Signed",
    "so.unsigned": "Not signed",
    "so.signed_by": "Signed by",
    "so.pending": "Pending sign-off",
    "so.no_pending": "Nothing to sign off",
    "so.review": "Review your hours below, then sign.",
    "so.shift_summary": "Shift Summary",
    "so.success": "Sign-off recorded. Thank you.",
    // Crews
    "crews.title": "Crews",
    "crews.subtitle": "Save & edit who was on each crew",
    "crews.new": "New Crew",
    "crews.edit": "Edit Crew",
    "crews.name": "Crew Name",
    "crews.foreman": "Foreman",
    "crews.unassigned": "— Unassigned —",
    "crews.members": "Members",
    "crews.save": "Save Crew",
    // Common
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.search": "Search",
    "common.category": "Category",
    "common.all": "All",
    "common.code": "Code",
    "common.name": "Name",
    "common.active": "Active",
    "common.actions": "Actions",
    "common.new": "+ New",
    "common.confirm_delete": "Delete?",
    "common.loading": "Loading…",
    "common.required": "*",
    // Ranch / equipment / punch (new)
    "tc.ranch": "Ranch",
    "tc.operating_q": "Will you be operating equipment today?",
    "tc.required_for_job": " (required for this job)",
    "tc.no_equipment": "No Equipment",
    "tc.yes_choose": "Yes — Choose",
    "tc.no_equipment_status": "No equipment",
    "tc.choose_option": "Choose an option",
    "tc.req_hint": "Select a Ranch, Category & Job Code to enable clock-in.",
    "tc.equip_selected_suffix": "equipment selected",
    // My Timecard
    "mt.title": "My Timecard",
    "mt.role": "Tractor Driver",
    "mt.on_clock": "On the clock",
    "mt.on_break": "On break",
    "mt.clock_in": "Clock In",
    "mt.clock_out": "Clock Out",
    "mt.ranch": "Ranch",
    "mt.category": "Category",
    "mt.job": "Job Code",
    "mt.pick_category": "Pick category",
    "mt.equipment": "equipment",
    "mt.end_break": "End break",
    "mt.select_first": "Select a ranch, category, and job code first.",
    "mt.end_break_first": "End your break before clocking out.",
    "mt.one_break": "Only one break at a time.",
    // Break timer
    "bt.rest": "Rest Break",
    "bt.meal": "Meal Period",
    "bt.complete": "complete",
    "bt.started": "Started",
    "bt.required": "Required",
    "bt.return_at": "Return at",
    "bt.min": "min",
    "bt.done_msg": "Your break is complete. You may now return to work.",
    "bt.incomplete": "Break not yet complete — please remain on your break.",
    "bt.end_break": "End Break",
    "bt.accidental": "Accidental Start",
    "bt.accidental_q": "Did you accidentally start your break?",
    "bt.yes_cancel": "Yes, cancel",
    "bt.no": "No",
    "bt.reminder": "Please enjoy your full break. Vinedresser encourages all employees to take their complete rest and meal periods in accordance with company policy and California labor laws.",
    // Approvals
    "appr.title": "Weekly Approvals",
    "appr.sub_foreman": "Approve your crew's week · locks records from edits",
    "appr.sub_admin": "Approve any crew's week · locks records from edits",
    "appr.week_start": "Week Starting (Mon)",
    "appr.crew": "Crew",
    "appr.my_crews": "My crews (all)",
    "appr.all_crews": "All crews",
    "appr.approve_week": "Approve Week",
    "appr.unlock": "Unlock",
    "appr.recent": "Recent Approvals",
    "appr.none": "No approvals yet",
    "appr.entries": "entries",
    "appr.locked": "locked",
    // Sign-off weekly
    "sow.title": "Weekly Sign-Off",
    "sow.sub": "One signature confirms the employee's full week",
    "sow.week_start": "Week Starting (Mon)",
    "sow.sign_week": "Sign Week",
    "sow.review_week": "Review the full week before signing",
    "sow.total": "Total",
    "sow.day_pending": "day(s) pending",
    "sow.total_week": "total this week",
    "sow.signed_suffix": "signed",
    // Timesheets
    "ts.title": "Timesheets",
    "ts.start": "Start",
    "ts.end": "End",
    "ts.load": "Load",
    "ts.edit": "Edit",
    "ts.edit_title": "Edit Timecard",
    "ts.clock_in": "Clock In",
    "ts.clock_out": "Clock Out",
    "ts.notes": "Notes",
    "ts.breaks": "Breaks",
    "ts.no_breaks": "No breaks recorded.",
    "ts.save": "Save Changes",
    "ts.no_entries": "No entries",
    "ts.locked_warn": "This card is locked (approved). Saving will override the approved values.",
    "ts.ranch": "Ranch",
    "ts.employee": "Employee",
    "ts.job": "Job",
    // Reports
    "er.title": "Equipment Reports",
    "er.sub": "Usage & utilization by employee, job, property & machine",
    "er.run": "Run Report",
    "er.total_hours": "Total Equipment Hours",
    "er.machines": "Machines Used",
    "er.segments": "Logged Segments",
    "er.by_equipment": "By Equipment",
    "er.by_employee": "By Employee",
    "er.by_job": "By Payroll Job Code",
    "er.by_property": "By Property",
    "er.by_type": "By Equipment Type",
    "er.by_week": "By Week",
    "er.utilization": "Utilization Summary",
    "er.operators": "Operators",
    "er.hours": "Hours",
    "er.equipment": "Equipment",
    "er.no_data": "No data",
    "er.all_employees": "All employees",
    "er.all_properties": "All properties",
    "er.all_jobs": "All jobs",
    "er.all_types": "All types",
    "er.all_equipment": "All equipment",
    // Tractor logins
    "tl.title": "Tractor Driver Logins",
    "tl.sub": "Give each Tractor Driver a self-service login to punch & add equipment",
    "tl.none": "No employees labeled \"Tractor Driver\" yet. Set an employee's Role to Tractor Driver on the Employees page.",
    "tl.email": "login email",
    "tl.password": "password",
    "tl.new_password": "new password",
    "tl.create": "Create",
    "tl.reset": "Reset",
    // Break audit
    "ba.title": "Break Audit Log",
    "ba.sub": "Every rest & meal period + accidental starts",
    "ba.all_events": "All events",
    "ba.completed": "Completed",
    "ba.accidental": "Accidental start",
    "ba.run": "Run",
    "ba.none": "No break records",
    "ba.accidental_count": "accidental starts",
    "ba.datetime": "Date / Time",
    "ba.employee": "Employee",
    "ba.type": "Type",
    "ba.event": "Event",
    "ba.duration": "Duration",
    "ba.device": "Device",
    // nav additions
    "nav.my_timecard": "My Timecard",
    "nav.driver_logins": "Driver Logins",
    "nav.equip_reports": "Equip Reports",
    "nav.break_audit": "Break Audit",
    // Mileage
    "mil.sub": "Personal vehicle · GPS tracked · coded to ranch",
    "mil.employee": "Employee", "mil.myself": "Myself / select…",
    "mil.ranch": "Driving To (Ranch) *", "mil.select_ranch": "Select ranch…",
    "mil.jobcode": "Job Code *", "mil.select_jobcode": "Select job code…",
    "mil.miles": "miles", "mil.tracking": "tracking",
    "mil.start": "Start Mileage", "mil.end": "End Mileage (Arrived)",
    "mil.trips": "Recent Trips", "mil.total": "mi total", "mil.no_trips": "No trips yet",
    "mil.date": "Date", "mil.driver": "Driver", "mil.ranch_col": "Ranch", "mil.job": "Job", "mil.miles_col": "Miles",
    "mil.err_ranch": "Select the ranch you're driving to first.",
    "mil.err_jobcode": "Select a job code first.",
    "mil.err_gps": "GPS not available on this device.",
    // Expenses
    "exps.title": "Expense Reports", "exps.tab_submit": "Submit Expense",
    "exps.tab_approval": "Weekly Approval", "exps.tab_dashboard": "Dashboard",
    "exps.take_photo": "Take Photo", "exps.upload": "Upload",
    "exps.scanning": "Reading receipt with AI…", "exps.attached": "Receipt attached",
    "exps.date": "Purchase Date", "exps.amount": "Amount ($)", "exps.vendor": "Vendor", "exps.card": "Card Last 4",
    "exps.employee": "Employee *", "exps.sel_employee": "Select employee…",
    "exps.ranch": "Ranch *", "exps.select": "Select…", "exps.expcat": "Expense Category *",
    "exps.jobcat": "Job Category *", "exps.jobcode": "Job Code", "exps.optional": "Optional…",
    "exps.block": "Block", "exps.equipment": "Equipment (optional)",
    "exps.reimb": "I used my personal funds and need to be reimbursed for this purchase.",
    "exps.reimb_method": "Reimbursement Method", "exps.payroll": "Payroll", "exps.check": "Company Check",
    "exps.notes": "Notes", "exps.submit": "Submit Expense", "exps.submitting": "Submitting…",
    "exps.submitted": "Expense submitted",
    "exps.err_read": "Could not read receipt. Enter details manually.",
    "exps.err_req": "Ranch, Job Category and Expense Category are required.",
    // Work List
    "wl.title": "Work List", "wl.sub": "Tasks from scouting and manual entry — assign, schedule and complete.",
    "wl.new": "New Task", "wl.active": "Active", "wl.open": "Open", "wl.scheduled": "Scheduled",
    "wl.inprogress": "In Progress", "wl.complete": "Complete", "wl.cancelled": "Cancelled",
    "wl.all_types": "All Types", "wl.none": "No tasks", "wl.from_scouting": "From Scouting",
    "wl.client_requests": "Client Requests", "wl.no_client_requests": "No open client requests",
    "wl.from_client": "From Client", "wl.approve_to_worklist": "Approve → Work List",
    "wl.start": "Start", "wl.complete_btn": "Complete", "wl.cancel": "Cancel",
    "wl.schedule": "Schedule", "wl.reschedule": "Reschedule", "wl.due": "Due", "wl.completed": "Completed",
    "wl.date": "Date", "wl.time": "Time", "wl.supervisor": "Supervisor", "wl.crew": "Crew",
    "wl.employees": "Employees / Operators", "wl.equipment": "Equipment", "wl.schedule_task": "Schedule Task",
    // Irrigator Log
    "il.title": "Irrigation Schedule", "il.week_of": "Week of", "il.loading": "Loading schedule…",
    "il.total_hours": "Total Hours", "il.sets": "Sets", "il.done": "Hrs Done",
    "il.log": "Log Hours", "il.none": "No irrigation scheduled this week",
    "il.date": "Irrigation Date", "il.hours": "Hours Run", "il.note": "Note (optional)",
    "il.offline": "Offline — will queue and sync later", "il.cancel": "Cancel", "il.save": "Save Log",
    "il.enter_hours": "Enter hours", "il.logged": "Hours logged",
    "il.queued": "No signal — log queued, will sync automatically", "il.gal_total": "gal total",
    // Field Sheet (spray work order screen)
    "fs.loading": "Loading field sheet…", "fs.back": "Spray Orders", "fs.print": "Print",
    "fs.wo": "Spray Work Order", "fs.client": "Client", "fs.vineyard": "Vineyard", "fs.date": "Date",
    "fs.treated": "Treated Acres", "fs.blocks": "Blocks", "fs.block": "Block", "fs.variety": "Variety",
    "fs.acres": "Acres", "fs.eor": "⚠ EVERY OTHER ROW", "fs.every_row": "Every Row",
    "fs.custom_rows": "Custom Rows", "fs.people": "People & Equipment", "fs.applicator": "Applicator",
    "fs.manager": "Manager", "fs.supervisor": "Supervisor", "fs.tractor": "Tractor", "fs.sprayer": "Sprayer",
    "fs.method": "Method", "fs.nozzles": "Nozzles", "fs.county": "County / Permit",
    "fs.calibration": "Calibration", "fs.target_gpa": "Target GPA", "fs.speed": "Speed",
    "fs.pressure": "Pressure", "fs.output": "Output", "fs.tank_loads": "Tank Loads",
    "fs.active": "Active Ingredient", "fs.epa": "EPA Reg. No.", "fs.signal": "Signal Word",
    "fs.rate": "Rate", "fs.per_tank": "Per Tank", "fs.ppe_req": "Required PPE",
    "fs.ppe_summary": "PPE Summary — All Materials", "fs.rei_line": "Re-Entry Interval",
    "fs.hours_after": "hours after application", "fs.phi_line": "PHI", "fs.days_before": "days before harvest",
    "fs.forecast": "Forecast", "fs.field_instr": "Field Instructions & Precautions",
    "fs.app_record": "Application Record (Actual)", "fs.start_end": "Start / End",
    "fs.tanks_used": "Tanks Used", "fs.actual_gpa": "Actual GPA", "fs.weather": "Weather",
    "fs.completed_by": "Completed By", "fs.signoff": "Sign-Off", "fs.prepared_by": "Prepared By",
    "fs.reviewed_by": "Reviewed By", "fs.approved_by": "Approved By", "fs.ack_by": "Acknowledged By",
    // Crop Estimates
    "crop.title": "Crop Estimates",
    "crop.sub": "Counts on 40 vines per block · 30 clusters per weight sample (lbs) · lag phase × multiplier until post-veraison weights take over.",
    "crop.all_ranches": "All Ranches", "crop.sampling_form": "Sampling Form", "crop.scan_form": "Scan Form",
    "crop.export": "Export XLSX", "crop.print_summary": "Print Summary",
    "crop.export_ranch": "Export Ranch", "crop.record_harvest": "Record Harvest", "crop.add_sample": "Add Sample",
    "tc.selected": "selected — actions apply to selected only",
    // Safety Training
    "nav.training": "Safety Training",
    "tr.title": "Safety Training",
    "tr.sub": "Weekly tailgates · annual requirements · sign-off records",
    "tr.this_week": "This Week", "tr.master": "Master Schedule", "tr.compliance": "Compliance", "tr.log": "Training Log",
    "tr.materials": "Materials Library", "tr.open_source": "Open Source ↗",
    "tr.mat_code": "Ref", "tr.mat_title": "Material", "tr.mat_who": "Who",
    "tr.quarterly": "FLC Quarterly", "tr.read_material": "Read Training Material",
    "tr.q_due": "Due", "tr.q_upcoming": "Upcoming", "tr.q_days_left": "days left",
    "tr.q_alert": "FLC quarterly supervisor safety training ({q}) not yet completed",
    "tr.week_of": "Week of", "tr.week": "Week",
    "tr.track_field": "All Other Employees", "tr.track_tractor": "Tractor Drivers",
    "tr.done": "Completed", "tr.auto": "Auto-selected", "tr.by_admin": "Set by admin",
    "tr.sheet_en": "Sign-Off Sheet", "tr.sheet_es": "Hoja ES", "tr.record": "Record Completion",
    "tr.attendees": "Attendees", "tr.date": "Date", "tr.trainer": "Trainer", "tr.notes": "Notes (optional)",
    "tr.save_session": "Save Training Record", "tr.recorded": "Training recorded",
    "tr.need_attendees": "Select at least one attendee",
    "tr.annual_due": "Annual / required training due",
    "tr.workers": "worker(s)", "tr.forecast": "Forecast", "tr.rain": "rain", "tr.wind": "wind",
    "tr.spray_week": "spray scheduled this week",
    "tr.auto_weather": "Auto (weather / task based)",
    "tr.employee": "Employee", "tr.topic": "Topic",
    "tr.never": "Never", "tr.overdue": "Overdue", "tr.due_soon": "Due Soon", "tr.no_sessions": "No trainings recorded yet",
    "tc.whole_crew": "Applies to whole crew — check workers to pick a group",
    "tc.clear_sel": "Clear",
  },
  es: {
    "app.tagline": "Operaciones de viñedo, todo en un solo lugar.",
    "app.compliance": "DIR · Cal Labor · Listo para QB",
    "auth.signin": "Iniciar Sesión",
    "auth.signing_in": "Iniciando sesión…",
    "auth.email": "Correo",
    "auth.password": "Contraseña",
    "auth.face_id": "Face ID / Touch ID",
    "auth.demo": "Cuentas de prueba",
    "auth.exit": "Salir",
    "nav.dashboard": "Tablero",
    "nav.punch_crew": "Control de Tiempo",
    "nav.crews": "Cuadrillas",
    "nav.employees": "Empleados",
    "nav.jobcodes": "Códigos de Trabajo",
    "nav.equipment": "Equipos",
    "nav.ranches": "Ranchos",
    "nav.import": "Importar Datos",
    "nav.users": "Usuarios",
    "nav.expenses": "Gastos",
    "nav.mileage": "Millaje",
    "nav.scouting": "Monitoreo de Campo",
    "nav.pest": "Plagas y Enfermedades",
    "nav.spray_orders": "Órdenes de Aspersión",
    "nav.crop": "Estimación de Cosecha",
    "nav.worklist": "Lista de Trabajo",
    "nav.clients": "Clientes",
    "nav.client_requests": "Solicitudes de Clientes",
    "nav.estate": "Cuidado de Fincas",
    "nav.portal": "Mis Viñedos",
    "nav.client_logins": "Accesos de Clientes",
    "nav.budgets": "Presupuestos",
    "nav.irrigation": "Riego",
    "nav.irrigation_log": "Registro de Riego",
    "nav.more": "Más",
    "nav.menu": "Menú",
    "nav.timesheets": "Hojas de Tiempo",
    "nav.qb_export": "Exportar QB",
    "nav.approvals": "Aprobaciones",
    "nav.signoff": "Firmar",
    "status.live": "En línea",
    "status.syncing": "Sincronizando…",
    "status.offline": "Sin conexión",
    "status.queued": "pendientes",
    "status.sync": "Sincronizar",
    "dash.operations": "Operaciones",
    "dash.todays_crew": "Cuadrilla de Hoy",
    "dash.regular_hrs": "Horas Regulares (Mes)",
    "dash.overtime_hrs": "Horas Extra",
    "dash.doubletime_hrs": "Horas Doble",
    "dash.active_employees": "Empleados Activos",
    "dash.penalties": "Penalidades del Código Laboral CA este Mes",
    "dash.meal_penalties": "Penalidades por comida",
    "dash.rest_penalties": "Penalidades por descanso",
    "dash.todays_entries": "Entradas de Hoy",
    "dash.category_totals": "Totales por Categoría (mes)",
    "dash.cat_hours_only": "Horas por categoría de trabajo",
    "dash.no_entries": "No hay entradas hoy",
    "dash.quick_punch": "Marcaje Rápido",
    "dash.quick_punch_sub": "Entrada / salida con un toque",
    "dash.on_clock": "Trabajando",
    "dash.off_clock": "Fuera de turno",
    "tc.title": "Control de Tiempo",
    "tc.subtitle": "Marcas de tiempo reales. Sin redondeo. Cumple con el Código Laboral de CA.",
    "tc.crew": "Cuadrilla",
    "tc.all_employees": "Todos los Empleados",
    "tc.members": "miembros",
    "tc.job_code": "Código de Trabajo (para nuevas marcas)",
    "tc.select_job": "— Seleccionar Trabajo —",
    "tc.category": "Categoría",
    "tc.select_category": "— Seleccionar Categoría —",
    "tc.select_category_first": "— Seleccione una categoría primero —",
    "tc.punch_in": "Hora de Inicio",
    "tc.punch_out": "Hora de Fin",
    "tc.meal": "Comida",
    "tc.rest": "Descanso",
    "tc.equip": "Equipo",
    "tc.bulk_actions": "Acciones de Cuadrilla",
    "tc.bulk_punch_in": "Inicio (Todos)",
    "tc.bulk_punch_out": "Fin (Todos)",
    "tc.bulk_punch_in_sel": "Inicio ({n} Seleccionados)",
    "tc.bulk_punch_out_sel": "Fin ({n} Seleccionados)",
    "tc.bulk_meal_start": "Iniciar Comida Todos",
    "tc.bulk_meal_end": "Fin Comida Todos",
    "tc.bulk_rest_start": "Iniciar Descanso Todos",
    "tc.bulk_rest_end": "Fin Descanso Todos",
    "tc.equipment_for": "Equipo ·",
    "tc.close": "Cerrar",
    "tc.required_equipment": "Este trabajo requiere equipo",
    "tc.no_equipment_ack": "Confirmo que no se usó equipo",
    "tc.in": "Entrada:",
    "tc.tab_crew": "Tiempo de Cuadrilla",
    "tc.tab_individual": "Tiempo Individual",
    "tc.search_employee": "Buscar empleado por nombre…",
    "tc.no_match": "Ningún empleado coincide con la búsqueda",
    "tc.change_job": "Cambiar Trabajo",
    "tc.bulk_change_job": "Cambiar Trabajo (Todos)",
    "tc.change_job_hint": "Cierra el trabajo actual e inicia uno nuevo en el rancho y código de trabajo seleccionados (divide el turno para un costeo preciso).",
    "tc.confirm_change_title": "Confirmar Cambio de Trabajo / Rancho",
    "tc.confirm_change_body": "Esto finaliza el trabajo actual e inicia uno nuevo con las selecciones de abajo. El turno se divide para un costeo preciso.",
    "tc.confirm_who": "Aplica A",
    "tc.confirm_change": "Cambiar Trabajo",
    "so.title": "Firma Diaria",
    "so.subtitle": "Firme su tarjeta de tiempo y confirme la seguridad del día",
    "so.your_name": "Escriba su nombre completo",
    "so.signature": "Firma",
    "so.clear": "Borrar",
    "so.no_injury": "NO sufrí lesiones durante mi turno de hoy.",
    "so.no_harassment": "NO experimenté ni presencié acoso o discriminación hoy (o lo reporté abajo).",
    "so.notes": "Opcional: reportar cualquier incidente o inquietud",
    "so.submit": "Enviar Firma",
    "so.signed": "Firmado",
    "so.unsigned": "Sin firmar",
    "so.signed_by": "Firmado por",
    "so.pending": "Pendiente firmar",
    "so.no_pending": "Nada para firmar",
    "so.review": "Revise sus horas abajo, luego firme.",
    "so.shift_summary": "Resumen del Turno",
    "so.success": "Firma registrada. Gracias.",
    "crews.title": "Cuadrillas",
    "crews.subtitle": "Guarde y edite quién está en cada cuadrilla",
    "crews.new": "Nueva Cuadrilla",
    "crews.edit": "Editar Cuadrilla",
    "crews.name": "Nombre",
    "crews.foreman": "Capataz",
    "crews.unassigned": "— Sin asignar —",
    "crews.members": "Miembros",
    "crews.save": "Guardar",
    "common.cancel": "Cancelar",
    "common.save": "Guardar",
    "common.delete": "Eliminar",
    "common.edit": "Editar",
    "common.search": "Buscar",
    "common.category": "Categoría",
    "common.all": "Todas",
    "common.code": "Código",
    "common.name": "Nombre",
    "common.active": "Activo",
    "common.actions": "Acciones",
    "common.new": "+ Nuevo",
    "common.confirm_delete": "¿Eliminar?",
    "common.loading": "Cargando…",
    "common.required": "*",
    "tc.ranch": "Rancho",
    "tc.operating_q": "¿Va a operar equipo hoy?",
    "tc.required_for_job": " (requerido para este trabajo)",
    "tc.no_equipment": "Sin Equipo",
    "tc.yes_choose": "Sí — Elegir",
    "tc.no_equipment_status": "Sin equipo",
    "tc.choose_option": "Elija una opción",
    "tc.req_hint": "Seleccione Rancho, Categoría y Código de Trabajo para habilitar la entrada.",
    "tc.equip_selected_suffix": "equipo(s) seleccionado(s)",
    "mt.title": "Mi Tarjeta",
    "mt.role": "Tractorista",
    "mt.on_clock": "Trabajando",
    "mt.on_break": "En descanso",
    "mt.clock_in": "Marcar Entrada",
    "mt.clock_out": "Marcar Salida",
    "mt.ranch": "Rancho",
    "mt.category": "Categoría",
    "mt.job": "Código de Trabajo",
    "mt.pick_category": "Elija categoría",
    "mt.equipment": "equipo(s)",
    "mt.end_break": "Terminar descanso",
    "mt.select_first": "Seleccione un rancho, categoría y código de trabajo primero.",
    "mt.end_break_first": "Termine su descanso antes de marcar salida.",
    "mt.one_break": "Solo un descanso a la vez.",
    "bt.rest": "Descanso",
    "bt.meal": "Período de Comida",
    "bt.complete": "completado",
    "bt.started": "Inició",
    "bt.required": "Requerido",
    "bt.return_at": "Regreso a las",
    "bt.min": "min",
    "bt.done_msg": "Su descanso está completo. Ya puede regresar al trabajo.",
    "bt.incomplete": "El descanso aún no termina — por favor permanezca en su descanso.",
    "bt.end_break": "Terminar Descanso",
    "bt.accidental": "Inicio Accidental",
    "bt.accidental_q": "¿Inició su descanso por accidente?",
    "bt.yes_cancel": "Sí, cancelar",
    "bt.no": "No",
    "bt.reminder": "Por favor disfrute su descanso completo. Vinedresser anima a todos los empleados a tomar sus períodos completos de descanso y comida de acuerdo con la política de la empresa y las leyes laborales de California.",
    "appr.title": "Aprobaciones Semanales",
    "appr.sub_foreman": "Apruebe la semana de su cuadrilla · bloquea los registros",
    "appr.sub_admin": "Apruebe la semana de cualquier cuadrilla · bloquea los registros",
    "appr.week_start": "Semana Inicia (Lun)",
    "appr.crew": "Cuadrilla",
    "appr.my_crews": "Mis cuadrillas (todas)",
    "appr.all_crews": "Todas las cuadrillas",
    "appr.approve_week": "Aprobar Semana",
    "appr.unlock": "Desbloquear",
    "appr.recent": "Aprobaciones Recientes",
    "appr.none": "Aún no hay aprobaciones",
    "appr.entries": "entradas",
    "appr.locked": "bloqueadas",
    "sow.title": "Firma Semanal",
    "sow.sub": "Una firma confirma la semana completa del empleado",
    "sow.week_start": "Semana Inicia (Lun)",
    "sow.sign_week": "Firmar Semana",
    "sow.review_week": "Revise la semana completa antes de firmar",
    "sow.total": "Total",
    "sow.day_pending": "día(s) pendiente(s)",
    "sow.total_week": "total esta semana",
    "sow.signed_suffix": "firmadas",
    "ts.title": "Hojas de Tiempo",
    "ts.start": "Inicio",
    "ts.end": "Fin",
    "ts.load": "Cargar",
    "ts.edit": "Editar",
    "ts.edit_title": "Editar Tarjeta",
    "ts.clock_in": "Entrada",
    "ts.clock_out": "Salida",
    "ts.notes": "Notas",
    "ts.breaks": "Descansos",
    "ts.no_breaks": "Sin descansos registrados.",
    "ts.save": "Guardar Cambios",
    "ts.no_entries": "Sin entradas",
    "ts.locked_warn": "Esta tarjeta está bloqueada (aprobada). Guardar anulará los valores aprobados.",
    "ts.ranch": "Rancho",
    "ts.employee": "Empleado",
    "ts.job": "Trabajo",
    "er.title": "Reportes de Equipo",
    "er.sub": "Uso y utilización por empleado, trabajo, propiedad y máquina",
    "er.run": "Generar Reporte",
    "er.total_hours": "Horas Totales de Equipo",
    "er.machines": "Máquinas Usadas",
    "er.segments": "Segmentos Registrados",
    "er.by_equipment": "Por Equipo",
    "er.by_employee": "Por Empleado",
    "er.by_job": "Por Código de Trabajo",
    "er.by_property": "Por Propiedad",
    "er.by_type": "Por Tipo de Equipo",
    "er.by_week": "Por Semana",
    "er.utilization": "Resumen de Utilización",
    "er.operators": "Operadores",
    "er.hours": "Horas",
    "er.equipment": "Equipo",
    "er.no_data": "Sin datos",
    "er.all_employees": "Todos los empleados",
    "er.all_properties": "Todas las propiedades",
    "er.all_jobs": "Todos los trabajos",
    "er.all_types": "Todos los tipos",
    "er.all_equipment": "Todo el equipo",
    "tl.title": "Accesos de Tractoristas",
    "tl.sub": "Dé a cada Tractorista un acceso para marcar y agregar equipo",
    "tl.none": "Aún no hay empleados con rol \"Tractorista\". Asigne el rol Tractorista en la página de Empleados.",
    "tl.email": "correo de acceso",
    "tl.password": "contraseña",
    "tl.new_password": "nueva contraseña",
    "tl.create": "Crear",
    "tl.reset": "Restablecer",
    "ba.title": "Registro de Descansos",
    "ba.sub": "Cada descanso y comida + inicios accidentales",
    "ba.all_events": "Todos los eventos",
    "ba.completed": "Completado",
    "ba.accidental": "Inicio accidental",
    "ba.run": "Buscar",
    "ba.none": "Sin registros de descanso",
    "ba.accidental_count": "inicios accidentales",
    "ba.datetime": "Fecha / Hora",
    "ba.employee": "Empleado",
    "ba.type": "Tipo",
    "ba.event": "Evento",
    "ba.duration": "Duración",
    "ba.device": "Dispositivo",
    "nav.my_timecard": "Mi Tarjeta",
    "nav.driver_logins": "Accesos Tractoristas",
    "nav.equip_reports": "Reportes Equipo",
    "nav.break_audit": "Registro Descansos",
    "mil.sub": "Vehículo personal · rastreado por GPS · codificado al rancho",
    "mil.employee": "Empleado", "mil.myself": "Yo mismo / seleccionar…",
    "mil.ranch": "Conduciendo a (Rancho) *", "mil.select_ranch": "Seleccione rancho…",
    "mil.jobcode": "Código de Trabajo *", "mil.select_jobcode": "Seleccione código…",
    "mil.miles": "millas", "mil.tracking": "rastreando",
    "mil.start": "Iniciar Millaje", "mil.end": "Terminar Millaje (Llegué)",
    "mil.trips": "Viajes Recientes", "mil.total": "mi en total", "mil.no_trips": "Sin viajes aún",
    "mil.date": "Fecha", "mil.driver": "Conductor", "mil.ranch_col": "Rancho", "mil.job": "Trabajo", "mil.miles_col": "Millas",
    "mil.err_ranch": "Seleccione primero el rancho al que conduce.",
    "mil.err_jobcode": "Seleccione primero un código de trabajo.",
    "mil.err_gps": "GPS no disponible en este dispositivo.",
    "exps.title": "Reportes de Gastos", "exps.tab_submit": "Enviar Gasto",
    "exps.tab_approval": "Aprobación Semanal", "exps.tab_dashboard": "Panel",
    "exps.take_photo": "Tomar Foto", "exps.upload": "Subir",
    "exps.scanning": "Leyendo recibo con IA…", "exps.attached": "Recibo adjunto",
    "exps.date": "Fecha de Compra", "exps.amount": "Monto ($)", "exps.vendor": "Proveedor", "exps.card": "Últimos 4 de Tarjeta",
    "exps.employee": "Empleado *", "exps.sel_employee": "Seleccione empleado…",
    "exps.ranch": "Rancho *", "exps.select": "Seleccione…", "exps.expcat": "Categoría de Gasto *",
    "exps.jobcat": "Categoría de Trabajo *", "exps.jobcode": "Código de Trabajo", "exps.optional": "Opcional…",
    "exps.block": "Bloque", "exps.equipment": "Equipo (opcional)",
    "exps.reimb": "Usé mis fondos personales y necesito reembolso por esta compra.",
    "exps.reimb_method": "Método de Reembolso", "exps.payroll": "Nómina", "exps.check": "Cheque de la Empresa",
    "exps.notes": "Notas", "exps.submit": "Enviar Gasto", "exps.submitting": "Enviando…",
    "exps.submitted": "Gasto enviado",
    "exps.err_read": "No se pudo leer el recibo. Ingrese los datos manualmente.",
    "exps.err_req": "Rancho, Categoría de Trabajo y Categoría de Gasto son obligatorios.",
    "wl.title": "Lista de Trabajo", "wl.sub": "Tareas del monitoreo y entrada manual — asigne, programe y complete.",
    "wl.new": "Nueva Tarea", "wl.active": "Activas", "wl.open": "Abiertas", "wl.scheduled": "Programadas",
    "wl.inprogress": "En Progreso", "wl.complete": "Completadas", "wl.cancelled": "Canceladas",
    "wl.all_types": "Todos los Tipos", "wl.none": "Sin tareas", "wl.from_scouting": "Del Monitoreo",
    "wl.client_requests": "Solicitudes de Clientes", "wl.no_client_requests": "Sin solicitudes abiertas de clientes",
    "wl.from_client": "Del Cliente", "wl.approve_to_worklist": "Aprobar → Lista de Trabajo",
    "wl.start": "Iniciar", "wl.complete_btn": "Completar", "wl.cancel": "Cancelar",
    "wl.schedule": "Programar", "wl.reschedule": "Reprogramar", "wl.due": "Vence", "wl.completed": "Completada",
    "wl.date": "Fecha", "wl.time": "Hora", "wl.supervisor": "Supervisor", "wl.crew": "Cuadrilla",
    "wl.employees": "Empleados / Operadores", "wl.equipment": "Equipo", "wl.schedule_task": "Programar Tarea",
    "il.title": "Calendario de Riego", "il.week_of": "Semana del", "il.loading": "Cargando calendario…",
    "il.total_hours": "Horas Totales", "il.sets": "Turnos", "il.done": "Hrs Hechas",
    "il.log": "Registrar Horas", "il.none": "Sin riego programado esta semana",
    "il.date": "Fecha de Riego", "il.hours": "Horas de Riego", "il.note": "Nota (opcional)",
    "il.offline": "Sin conexión — se guardará y sincronizará después", "il.cancel": "Cancelar", "il.save": "Guardar Registro",
    "il.enter_hours": "Ingrese horas", "il.logged": "Horas registradas",
    "il.queued": "Sin señal — registro en cola, se sincronizará automáticamente", "il.gal_total": "gal en total",
    "fs.loading": "Cargando hoja de campo…", "fs.back": "Órdenes de Aspersión", "fs.print": "Imprimir",
    "fs.wo": "Orden de Trabajo de Aspersión", "fs.client": "Cliente", "fs.vineyard": "Viñedo", "fs.date": "Fecha",
    "fs.treated": "Acres Tratados", "fs.blocks": "Bloques", "fs.block": "Bloque", "fs.variety": "Variedad",
    "fs.acres": "Acres", "fs.eor": "⚠ HILERA DE POR MEDIO", "fs.every_row": "Cada Hilera",
    "fs.custom_rows": "Hileras Personalizadas", "fs.people": "Personal y Equipo", "fs.applicator": "Aplicador",
    "fs.manager": "Gerente", "fs.supervisor": "Supervisor", "fs.tractor": "Tractor", "fs.sprayer": "Aspersora",
    "fs.method": "Método", "fs.nozzles": "Boquillas", "fs.county": "Condado / Permiso",
    "fs.calibration": "Calibración", "fs.target_gpa": "GPA Objetivo", "fs.speed": "Velocidad",
    "fs.pressure": "Presión", "fs.output": "Salida", "fs.tank_loads": "Cargas de Tanque",
    "fs.active": "Ingrediente Activo", "fs.epa": "No. Reg. EPA", "fs.signal": "Palabra de Señal",
    "fs.rate": "Dosis", "fs.per_tank": "Por Tanque", "fs.ppe_req": "EPP Requerido",
    "fs.ppe_summary": "Resumen de EPP — Todos los Materiales", "fs.rei_line": "Intervalo de Reingreso",
    "fs.hours_after": "horas después de la aplicación", "fs.phi_line": "PHI", "fs.days_before": "días antes de la cosecha",
    "fs.forecast": "Pronóstico", "fs.field_instr": "Instrucciones de Campo y Precauciones",
    "fs.app_record": "Registro de Aplicación (Real)", "fs.start_end": "Inicio / Fin",
    "fs.tanks_used": "Tanques Usados", "fs.actual_gpa": "GPA Real", "fs.weather": "Clima",
    "fs.completed_by": "Completado Por", "fs.signoff": "Firmas", "fs.prepared_by": "Preparado Por",
    "fs.reviewed_by": "Revisado Por", "fs.approved_by": "Aprobado Por", "fs.ack_by": "Recibido Por",
    "crop.title": "Estimación de Cosecha",
    "crop.sub": "Conteos de 40 vides por bloque · 30 racimos por muestra de peso (lbs) · fase lag × multiplicador hasta que dominen los pesos post-envero.",
    "crop.all_ranches": "Todos los Ranchos", "crop.sampling_form": "Formulario de Muestreo", "crop.scan_form": "Escanear Formulario",
    "crop.export": "Exportar XLSX", "crop.print_summary": "Imprimir Resumen",
    "crop.export_ranch": "Exportar Rancho", "crop.record_harvest": "Registrar Cosecha", "crop.add_sample": "Agregar Muestra",
    "tc.selected": "seleccionados — las acciones aplican solo a los seleccionados",
    "nav.training": "Capacitación",
    "tr.title": "Capacitación de Seguridad",
    "tr.sub": "Pláticas semanales · requisitos anuales · registros de firmas",
    "tr.this_week": "Esta Semana", "tr.master": "Calendario Maestro", "tr.compliance": "Cumplimiento", "tr.log": "Registro",
    "tr.materials": "Biblioteca de Materiales", "tr.open_source": "Abrir Fuente ↗",
    "tr.mat_code": "Ref", "tr.mat_title": "Material", "tr.mat_who": "Quién",
    "tr.quarterly": "Trimestral FLC", "tr.read_material": "Leer Material de Capacitación",
    "tr.q_due": "Pendiente", "tr.q_upcoming": "Próximo", "tr.q_days_left": "días restantes",
    "tr.q_alert": "Capacitación trimestral FLC de supervisores ({q}) aún no completada",
    "tr.week_of": "Semana del", "tr.week": "Semana",
    "tr.track_field": "Todos los Demás Empleados", "tr.track_tractor": "Operadores de Tractor",
    "tr.done": "Completada", "tr.auto": "Auto-seleccionada", "tr.by_admin": "Asignada por admin",
    "tr.sheet_en": "Hoja de Firmas EN", "tr.sheet_es": "Hoja de Firmas ES", "tr.record": "Registrar Capacitación",
    "tr.attendees": "Asistentes", "tr.date": "Fecha", "tr.trainer": "Capacitador", "tr.notes": "Notas (opcional)",
    "tr.save_session": "Guardar Registro", "tr.recorded": "Capacitación registrada",
    "tr.need_attendees": "Seleccione al menos un asistente",
    "tr.annual_due": "Capacitación anual / requerida pendiente",
    "tr.workers": "trabajador(es)", "tr.forecast": "Pronóstico", "tr.rain": "lluvia", "tr.wind": "viento",
    "tr.spray_week": "aspersión programada esta semana",
    "tr.auto_weather": "Auto (según clima / tarea)",
    "tr.employee": "Empleado", "tr.topic": "Tema",
    "tr.never": "Nunca", "tr.overdue": "Vencida", "tr.due_soon": "Pronto", "tr.no_sessions": "Sin capacitaciones registradas",
    "tc.whole_crew": "Aplica a toda la cuadrilla — marque trabajadores para elegir un grupo",
    "tc.clear_sel": "Quitar",
  },
};

const I18nContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(localStorage.getItem("ct_lang") || "en");
  const setLang = (l) => { localStorage.setItem("ct_lang", l); setLangState(l); };
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const t = (k) => DICT[lang]?.[k] ?? DICT.en[k] ?? k;
  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

// Bilingual display for imported data (job categories / descriptions).
// In Spanish mode shows "Español · English"; otherwise English only.
export function bilingual(en, es, lang) {
  const a = (en || "").trim();
  const b = (es || "").trim();
  if (lang === "es" && b && b.toLowerCase() !== a.toLowerCase()) {
    return a ? `${b} · ${a}` : b;
  }
  return a || b || "";
}
